import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, Send, Paperclip, X, Download, AlertCircle, CheckCircle2 } from 'lucide-react'
import { formatLocalTime } from '@/lib/date-utils'
import {
  getPortalConversation,
  pollPortalMessages,
  sendPortalMessage,
  uploadPortalAttachment,
  getPortalAttachmentUrl,
  setPortalTypingIndicator,
  type PortalMessage,
  type PortalConversationResponse
} from '@/lib/portal-api'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface PendingAttachment {
  id: string
  file: File
  uploading: boolean
  error?: string
  attachmentId?: string
}

function PortalChat() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const tokenFromUrl = searchParams.get('token')
  const tokenFromStorage = localStorage.getItem('portal_token')
  const portalToken = tokenFromUrl || tokenFromStorage

  // Store token in localStorage if it's in the URL
  useEffect(() => {
    if (tokenFromUrl) {
      localStorage.setItem('portal_token', tokenFromUrl)
    }
  }, [tokenFromUrl])

  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [conversation, setConversation] = useState<PortalConversationResponse | null>(null)
  const [messages, setMessages] = useState<PortalMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [sessionExpired, setSessionExpired] = useState(false)
  const [conversationEnded, setConversationEnded] = useState(false)
  const [isAgentTyping, setIsAgentTyping] = useState(false)
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastMessageIdRef = useRef<string | null>(null)
  const conversationRef = useRef<PortalConversationResponse | null>(null)
  const isPollingRef = useRef(false)
  const lastTypingCallRef = useRef<number>(0)
  const stopPollingRef = useRef(false)

  useEffect(() => {
    if (!portalToken) {
      navigate('/portal')
      return
    }

    loadConversation()

    // Clean up on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      if (typingTimeout) {
        clearTimeout(typingTimeout)
      }
    }
  }, [portalToken])

  // Clear polling when conversation ends
  useEffect(() => {
    if (conversationEnded || sessionExpired) {
      stopPollingRef.current = true
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [conversationEnded, sessionExpired])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    // Scroll to bottom when agent starts typing
    if (isAgentTyping) {
      scrollToBottom()
    }
  }, [isAgentTyping])

  const scrollToBottom = () => {
    // Use instant scrolling on initial load, smooth for new messages
    const behavior = messages.length <= 1 ? 'instant' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior: behavior as ScrollBehavior })
  }

  const loadConversation = async () => {
    if (!portalToken) return

    try {
      const data = await getPortalConversation(portalToken)
      setConversation(data)
      conversationRef.current = data // Store in ref for immediate access
      
      // Store app_id from conversation data
      if (data.app?.app_id) {
        localStorage.setItem('portal_app_id', data.app.app_id)
        sessionStorage.setItem('portal_app_id', data.app.app_id)
      }

      // Check if conversation is closed or resolved
      if (data.conversation.status === 'closed' || data.conversation.is_resolved) {
        if (data.conversation.is_resolved) {
          setConversationEnded(true)
          stopPollingRef.current = true
        } else {
          setSessionExpired(true)
          stopPollingRef.current = true
        }
      } else {
        // Load initial messages - pass the token directly since state might not be updated yet
        try {
          const initialMessages = await pollPortalMessages(portalToken)
          if (initialMessages.messages.length > 0) {
            setMessages(initialMessages.messages)
            lastMessageIdRef.current = initialMessages.messages[initialMessages.messages.length - 1].message_id
            // Scroll to bottom after initial messages load
            setTimeout(() => scrollToBottom(), 100)
          }
        } catch (error) {
          console.error('Failed to load initial messages:', error)
        }

        // Start polling for active conversations
        // Clear any existing interval first
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
        }
        pollIntervalRef.current = setInterval(() => {
          pollMessages()
        }, 3000) // Poll every 3 seconds
      }
    } catch (error) {
      console.error('Failed to load conversation:', error)
      const err = error as any
      const errorCode = err?.originalResponse?.error?.code
      const statusCode = err?.originalResponse?.status || err?.status
      if (statusCode === 403 || errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN' || errorCode === 'CONVERSATION_CLOSED' || errorCode === 'CONVERSATION_RESOLVED') {
        // Check if we had a conversation ID stored - if so, this was likely a resolved conversation
        const storedConversationId = localStorage.getItem('conversation_id')
        const storedAppId = localStorage.getItem('portal_app_id')
        
        // If we have a stored conversation ID and the token matches what we stored, 
        // this is likely a resolved conversation rather than an invalid token
        if (storedConversationId && portalToken === localStorage.getItem('portal_token')) {
          setConversationEnded(true)
          stopPollingRef.current = true
          // Clear any polling interval that might have been set up
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
          }
          // Still need to load some basic data for display
          if (storedAppId) {
            setConversation({
              app: { app_id: storedAppId, app_name: 'Support' },
              company: { company_id: '', company_name: '' },
              conversation: { conversation_id: storedConversationId },
              customer: {},
              queue: null,
              assigned_user: null,
              statistics: {},
              portal_session: {},
              endpoints: {}
            } as any)
          }
          setLoading(false)
          return
        }
        
        // Clear the expired token and conversation ID
        localStorage.removeItem('portal_token')
        localStorage.removeItem('conversation_id')

        // Store app_id if provided in error details
        const appId = err?.originalResponse?.error?.details?.app_id || storedAppId
        if (appId) {
          localStorage.setItem('portal_app_id', appId)
          sessionStorage.setItem('portal_app_id', appId)
        }

        // Redirect to portal
        const redirectUrl = appId ? `/portal?app_id=${appId}` :
          localStorage.getItem('portal_app_id') ? `/portal?app_id=${localStorage.getItem('portal_app_id')}` :
            '/portal'
        navigate(redirectUrl)
        return
      }
      toast.error('Failed to load conversation. Please try again.')
      // Don't start polling if conversation load failed
    } finally {
      setLoading(false)
    }
  }

  const pollMessages = async () => {
    // Check if polling has been stopped
    if (stopPollingRef.current) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    // Check if we should poll
    if (!portalToken || sessionExpired || conversationEnded || (!conversation && !conversationRef.current)) {
      // Clear the interval if conditions aren't met
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      return
    }

    // Prevent duplicate polling
    if (isPollingRef.current) return
    isPollingRef.current = true

    try {
      const response = await pollPortalMessages(portalToken, lastMessageIdRef.current || undefined)


      if (response.messages.length > 0) {
        setMessages(prev => [...prev, ...response.messages])
        lastMessageIdRef.current = response.messages[response.messages.length - 1].message_id
      }

      // Update typing indicator
      if (response.is_agent_typing !== undefined) {
        setIsAgentTyping(response.is_agent_typing)
      }

      // Update conversation status if changed
      if (response.conversation_status === 'closed') {
        setSessionExpired(true)
        stopPollingRef.current = true
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
      }

      // Update session expiry in conversation
      if (conversation) {
        setConversation({
          ...conversation,
          portal_session: {
            ...conversation.portal_session,
            expires_ts: response.session_expires_ts
          }
        })
      }
    } catch (error) {
      console.error('Failed to poll messages:', error)
      const err = error as any
      
      // Check for 403 error or token expiration
      const errorCode = err?.originalResponse?.error?.code
      const statusCode = err?.status || err?.originalResponse?.status

      // If we get a 403 with CONVERSATION_RESOLVED, or any 403 during polling, conversation was resolved
      if (statusCode === 403 || errorCode === 'CONVERSATION_RESOLVED') {
        setConversationEnded(true)
        stopPollingRef.current = true
        // Clear the interval immediately
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        // Don't clear the token from localStorage - we still need it for display purposes
        // But clear it from the URL to prevent confusion
        if (tokenFromUrl) {
          navigate('/portal/chat', { replace: true })
        }
        return
      }

      // For other 403 errors or token issues on initial load
      if (statusCode === 403 || errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN' ||
        (error instanceof Error && (error.message.includes('expired') || error.message.includes('closed') || error.message.includes('invalid')))) {

        // Clear the expired token
        localStorage.removeItem('portal_token')

        // Store app_id if provided in error details
        const appId = err?.originalResponse?.error?.details?.app_id || conversation?.app?.app_id
        if (appId) {
          localStorage.setItem('portal_app_id', appId)
          sessionStorage.setItem('portal_app_id', appId)
        }

        // Clear the interval
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }

        // Redirect to portal
        const redirectUrl = appId ? `/portal?app_id=${appId}` : '/portal'
        navigate(redirectUrl)
      }
    } finally {
      isPollingRef.current = false
    }
  }

  const handleTyping = () => {
    if (!portalToken || sessionExpired || conversationEnded) return

    const now = Date.now()
    const timeSinceLastCall = now - lastTypingCallRef.current

    // Only send typing indicator if it's been more than 3 seconds since the last call
    if (timeSinceLastCall >= 3000) {
      // Update the last call timestamp immediately to prevent multiple calls
      lastTypingCallRef.current = now

      // Send typing indicator
      setPortalTypingIndicator(portalToken).catch(err => {
        console.error("Failed to set typing indicator:", err)
      })
    }

    // Clear existing timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout)
    }

    // Set new timeout to clear typing after 10 seconds of no typing
    const timeout = setTimeout(() => {
      setTypingTimeout(null)
      // Reset last typing call time when user stops typing
      lastTypingCallRef.current = 0
    }, 10000)

    setTypingTimeout(timeout)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewMessage(value)

    // Trigger typing indicator if there's content
    if (value.trim()) {
      handleTyping()
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newMessage.trim() && pendingAttachments.length === 0) return
    if (!portalToken || sending || sessionExpired || conversationEnded) return

    // Clear typing timeout when sending
    if (typingTimeout) {
      clearTimeout(typingTimeout)
      setTypingTimeout(null)
    }

    setSending(true)
    try {
      // Get successfully uploaded attachment IDs
      const attachmentIds = pendingAttachments
        .filter(a => a.attachmentId)
        .map(a => a.attachmentId!)

      await sendPortalMessage(portalToken, newMessage.trim(), attachmentIds)

      setNewMessage('')
      setPendingAttachments([])

      // Immediately poll for the new message
      await pollMessages()
    } catch (error) {
      console.error('Failed to send message:', error)
      toast.error('Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    const newAttachments: PendingAttachment[] = files.map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      uploading: true
    }))

    setPendingAttachments(prev => [...prev, ...newAttachments])

    // Upload files
    for (const attachment of newAttachments) {
      try {
        const response = await uploadPortalAttachment(portalToken!, attachment.file)
        setPendingAttachments(prev =>
          prev.map(a =>
            a.id === attachment.id
              ? { ...a, uploading: false, attachmentId: response.attachment_id }
              : a
          )
        )
      } catch (error) {
        console.error('Failed to upload attachment:', error)
        setPendingAttachments(prev =>
          prev.map(a =>
            a.id === attachment.id
              ? { ...a, uploading: false, error: 'Failed to upload' }
              : a
          )
        )
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removeAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== id))
  }

  const handleStartNewConversation = () => {
    // Clear previous session data
    localStorage.removeItem('portal_token')
    localStorage.removeItem('conversation_id')
    
    // Navigate to portal with app_id if available and mark as new conversation
    const appId = conversation?.app?.app_id || localStorage.getItem('portal_app_id')
    const url = appId ? `/portal?app_id=${appId}&new=true` : '/portal?new=true'
    window.location.href = url
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  if (loading) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-gray-50">
        <Card className="p-6">
          <p className="text-center text-gray-600">Failed to load conversation</p>
          <Button
            onClick={handleStartNewConversation}
            className="mt-4 w-full h-10 px-4 py-2"
          >
            Start New Conversation
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-[100dvh] bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-3 sm:px-4 py-3 sm:py-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{conversation?.app?.app_name || 'Support'}</h1>
            <p className="text-sm sm:text-base text-gray-600 truncate">
              {conversation?.company?.company_name || ''}
              {conversation?.queue?.queue_name && ` • ${conversation.queue.queue_name}`}
            </p>
          </div>
          {(sessionExpired || conversationEnded) && (
            <div className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base text-red-600 flex-shrink-0">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">{conversationEnded ? 'Conversation Ended' : 'Session Expired'}</span>
              <span className="sm:hidden">{conversationEnded ? 'Ended' : 'Expired'}</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden min-h-0">
        <ScrollArea className="h-full p-3 sm:p-4">
          <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
          {messages.map((message) => (
            <div
              key={message.message_id}
              className={cn(
                'flex',
                message.sender_type === 'customer' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'max-w-[85%] sm:max-w-[70%] space-y-2',
                  message.sender_type === 'customer' ? 'items-end' : 'items-start'
                )}
              >
                <div
                  className={cn(
                    'rounded-lg px-3 py-2 sm:px-4',
                    message.sender_type === 'customer'
                      ? 'bg-blue-600 text-white'
                      : message.sender_type === 'auto'
                      ? 'bg-gray-100 border border-gray-300 shadow-sm italic'
                      : 'bg-white border border-gray-200 shadow-sm'
                  )}
                >
                  {message.sender_type !== 'customer' && (
                    <p className="text-sm font-medium mb-1 text-gray-700 truncate">
                      {message.sender_type === 'auto' ? 'Automated Response' : message.sender_name}
                    </p>
                  )}
                  <p className={cn(
                    "text-sm sm:text-base whitespace-pre-wrap break-words",
                    message.sender_type === 'customer' ? 'text-white' : 'text-gray-900'
                  )}>{message.content}</p>
                </div>

                {message.attachments.length > 0 && (
                  <div className="space-y-1">
                    {message.attachments.map((attachment) => (
                      <a
                        key={attachment.attachment_id}
                        href={getPortalAttachmentUrl(attachment.attachment_id, portalToken!)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded max-w-full',
                          message.sender_type === 'customer'
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        )}
                      >
                        <Download className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{attachment.original_filename}</span>
                        <span className="text-muted-foreground flex-shrink-0 text-sm">
                          ({formatFileSize(attachment.file_size)})
                        </span>
                      </a>
                    ))}
                  </div>
                )}

                <p className="text-sm text-gray-500">
                  {formatLocalTime(message.created_ts)}
                </p>
              </div>
            </div>
          ))}
          {/* Typing indicator */}
          {isAgentTyping && (
            <div className="flex justify-start">
              <div className="max-w-[85%] sm:max-w-[70%]">
                <div className="rounded-lg px-3 sm:px-4 py-2 bg-white border border-gray-200 shadow-sm">
                  <p className="text-sm font-medium mb-1 text-gray-700 truncate">
                    {conversation?.assigned_user?.name || 'Agent'}
                  </p>
                  <div className="flex items-center gap-1">
                    <span className="animate-bounce inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full" style={{ animationDelay: '0ms' }}></span>
                    <span className="animate-bounce inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full" style={{ animationDelay: '150ms' }}></span>
                    <span className="animate-bounce inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-3">typing...</p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Conversation Ended Banner */}
      {conversationEnded && (
        <div className="bg-yellow-50 border-t border-yellow-200 p-3 sm:p-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <p className="text-sm sm:text-base font-medium text-yellow-800">This conversation has ended</p>
                <p className="text-sm text-yellow-700 mt-0.5">The agent has resolved this conversation</p>
              </div>
            </div>
            <Button
              onClick={handleStartNewConversation}
              className="bg-yellow-600 text-white hover:bg-yellow-700 h-10 sm:h-11 px-4 sm:px-5 text-sm sm:text-base w-full sm:w-auto"
            >
              Start New Conversation
            </Button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="bg-gray-50 border-t border-gray-200 p-3 sm:p-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto">
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {pendingAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm',
                    attachment.error
                      ? 'bg-red-100 text-red-700'
                      : attachment.uploading
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-green-100 text-green-700'
                  )}
                >
                  {attachment.uploading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : attachment.error ? (
                    <AlertCircle className="h-3 w-3" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3" />
                  )}
                  <span className="max-w-[150px] truncate">{attachment.file.name}</span>
                  <button
                    onClick={() => removeAttachment(attachment.id)}
                    className="hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSendMessage} className="flex gap-2 sm:gap-3 items-center">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            />

            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sessionExpired || conversationEnded}
              className="bg-white border-gray-200 hover:bg-gray-100 h-9 w-9 sm:h-10 sm:w-10"
            >
              <Paperclip className="h-4 w-4 text-gray-500" />
            </Button>

            <Input
              value={newMessage}
              onChange={handleInputChange}
              placeholder={sessionExpired ? "Session expired" : conversationEnded ? "Conversation ended" : "Type your message..."}
              disabled={sessionExpired || conversationEnded}
              className="flex-1 bg-white text-gray-900 placeholder-gray-500 border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-base"
            />

            <Button
              type="submit"
              disabled={(!newMessage.trim() && pendingAttachments.length === 0) || sending || sessionExpired || conversationEnded}
              className="bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300 h-9 sm:h-10 px-3 sm:px-4"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>

          {sessionExpired && (
            <div className="mt-3 sm:mt-4 text-center">
              <p className="text-sm sm:text-base text-gray-600 mb-2">
                Your session has expired or the conversation has been closed.
              </p>
              <Button
                onClick={handleStartNewConversation}
                variant="outline"
                className="h-10 sm:h-11 px-4 sm:px-5 py-2 text-sm sm:text-base"
              >
                Start New Conversation
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { PortalChat }